import styled from 'styled-components';
import { ctaCss } from 'v3/components/FlowScreen/surfaces';

/* Layout for the "Getting started" screen. The dark surfaces it is built from are
   shared with the other steps and live in FlowScreen/surfaces; they are re-exported
   here so this module stays the single import for step 0's styles. */
export {
  Card,
  ConsentBox,
  DarkFields,
  Divide,
  Eyebrow,
  FieldHint,
  SectionSubtitle,
  SectionTitle,
  Subtitle,
  Title,
} from 'v3/components/FlowScreen/surfaces';

/* Top-aligned: the page is always taller than the viewport, and centring made the
   card start clipped at the top. */
export const Center = styled.div`
  position: relative;
  z-index: 10;
  margin: 0 auto;
  display: flex;
  width: 100%;
  max-width: 720px;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  gap: 24px;
  padding: 32px 0;
`;

export const Fields = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const FieldCol = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #e5e5e5;

  > span {
    color: rgba(255, 255, 255, 0.6);
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #ff9a9a;
`;

/* Phone: country picker + number share one dark fieldset box. Pairs with the
   global .sc-dark-fieldset class (border/background/focus ring). */
/* Same height as .sc-dark-field: here the spacing belongs to the box rather than
   the input, so the height is fixed and the content centres. */
export const PhoneBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
`;

export const Divider = styled.span`
  height: 20px;
  width: 1px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.15);
`;

export const CountryTrigger = styled.span`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;

  select {
    position: absolute;
    inset: 0;
    width: 100%;
    cursor: pointer;
    opacity: 0;
  }
`;

export const CtaRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* The colours and radius come from `ctaCss`, which steps 1 and 2 now use too. Only the
   width behaviour is step 0's own: full width on a phone, hugging its label above 640px. */
export const Cta = styled.button`
  ${ctaCss}

  width: 100%;

  @media (min-width: 640px) {
    width: auto;
  }
`;

/* ------------------------------------------------------------------------- *
 * Sections absorbed from the details panel (gender, address, SSN, consent)
 * ------------------------------------------------------------------------- */

export const Section = styled.section`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FcraDisclaimer = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #9a9aa8;
`;

/* The SMS opt-in reads in two layers: the sentence the person actually decides on,
   and the carrier boilerplate under it. The boilerplate is not written to be read,
   it is written so a carrier reviewer can find each required item in a screenshot,
   so it gets the smaller, quieter treatment.

   Both need `&&`. ConsentBox declares `p { font-size: 14px }` and darkCheckboxCss
   declares `p, label { color: #e5e5e5 }`; a descendant selector (0,1,1) outranks a
   single generated class (0,1,0), and doubling the ampersand wins it back. */
export const SmsCheckboxHint = styled.p`
  && {
    color: #9a9aa8;
    padding-left: 40px;
  }
`;

export const SmsFinePrint = styled.p`
  && {
    /* 40px matches Checkbox's Container inset, so the text lines up with the label
       above it instead of with the box. */
    margin: 8px 0 0;
    padding-left: 40px;
    font-size: 12px;
    line-height: 1.5;
    color: #9a9aa8;
  }

  a {
    color: #b9c0ff;
    text-decoration: underline;
  }
`;

/* Step 0 is the first screen of the wizard — there is nowhere to go back to, so the
   footer holds only the submit. The original CtaRow right alignment is kept. */
export const LoadingCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;
